// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Relatorios/RelatoriosGerencial.tsx
//  Relatórios Gerenciais: Curva ABC, Lucratividade, Evolução por Produto.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { BarChart3, Package, TrendingUp, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useAbcClientes, useAbcProdutos, useLucratividade, useEvolucaoProdutos,
} from '@/features/relatorios/hooks/useRelatorios';
import { BotaoExportar } from '@/components/BotaoExportar';

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const fmtNum = (v: number, d = 2) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v);
const today    = () => new Date().toISOString().slice(0, 10);
const anoInicio = () => `${new Date().getFullYear()}-01-01`;

const ABC_COLORS: Record<string, string> = {
  A: 'bg-green-500/10 text-green-600 border-green-300',
  B: 'bg-yellow-500/10 text-yellow-600 border-yellow-300',
  C: 'bg-red-500/10 text-red-600 border-red-300',
};

function FiltrosPeriodo({ dtInicio, dtFim, setDtInicio, setDtFim }: {
  dtInicio: string; dtFim: string;
  setDtInicio: (v: string) => void; setDtFim: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">De</span>
        <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} className="w-40" />
      </div>
      <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Até</span>
        <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} className="w-40" />
      </div>
    </div>
  );
}

// ── ABC Clientes ──────────────────────────────────────────────────────────────

function TabAbcClientes() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(today());
  const { data, isLoading } = useAbcClientes({ dtInicio, dtFim });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  const countA = data?.filter((x) => x.classificacao === 'A').length ?? 0;
  const countB = data?.filter((x) => x.classificacao === 'B').length ?? 0;
  const countC = data?.filter((x) => x.classificacao === 'C').length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={setDtInicio} setDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/gerencial/abc-clientes/exportar" params={exportParams} />
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3 text-sm">
          {[['A', countA, 'text-green-600'], ['B', countB, 'text-yellow-600'], ['C', countC, 'text-red-600']].map(([cls, cnt, color]) => (
            <div key={cls} className="rounded-lg border p-3 text-center">
              <p className={`text-2xl font-bold ${color}`}>{cls}</p>
              <p className="text-muted-foreground">{cnt} cliente{Number(cnt) !== 1 ? 's' : ''}</p>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead className="text-right">Qtd</TableHead>
              <TableHead className="text-right">Total Vendas</TableHead>
              <TableHead className="text-right">%</TableHead>
              <TableHead className="text-right">% Acumulado</TableHead>
              <TableHead className="text-center">Classe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.length ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.map((r) => (
              <TableRow key={r.clienteId}>
                <TableCell className="text-muted-foreground">{r.rank}</TableCell>
                <TableCell className="font-medium">{r.nomeCliente}</TableCell>
                <TableCell className="text-right">{r.qtdDocumentos}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalVendas)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.percentual)}%</TableCell>
                <TableCell className="text-right tabular-nums">
                  <div className="flex items-center justify-end gap-2">
                    <Progress value={r.percentualAcumulado} className="h-1.5 w-20" />
                    {fmtNum(r.percentualAcumulado)}%
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={ABC_COLORS[r.classificacao]}>{r.classificacao}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── ABC Produtos ──────────────────────────────────────────────────────────────

function TabAbcProdutos() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(today());
  const { data, isLoading } = useAbcProdutos({ dtInicio, dtFim });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={setDtInicio} setDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/gerencial/abc-produtos/exportar" params={exportParams} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead className="text-right">Qtd Vendida</TableHead>
              <TableHead className="text-right">Total Vendas</TableHead>
              <TableHead className="text-right">%</TableHead>
              <TableHead className="text-right">% Acumulado</TableHead>
              <TableHead className="text-center">Classe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.length ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.map((r) => (
              <TableRow key={r.produtoId}>
                <TableCell className="text-muted-foreground">{r.rank}</TableCell>
                <TableCell className="font-medium">{r.descricao}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.qtdVendida)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalVendas)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.percentual)}%</TableCell>
                <TableCell className="text-right tabular-nums">
                  <div className="flex items-center justify-end gap-2">
                    <Progress value={r.percentualAcumulado} className="h-1.5 w-20" />
                    {fmtNum(r.percentualAcumulado)}%
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={ABC_COLORS[r.classificacao]}>{r.classificacao}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Lucratividade ─────────────────────────────────────────────────────────────

function TabLucratividade() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(today());
  const { data, isLoading } = useLucratividade({ dtInicio, dtFim });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={setDtInicio} setDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/gerencial/lucratividade/exportar" params={exportParams} />
      </div>
      {data?.sintetico && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Vendas',   value: fmtCur(data.sintetico.totalVendas),    color: 'text-blue-600' },
            { label: 'Custo Total',    value: fmtCur(data.sintetico.totalCusto),     color: 'text-red-600' },
            { label: 'Lucro Bruto',    value: fmtCur(data.sintetico.lucroBrutoTotal), color: 'text-green-600' },
            { label: 'Margem Média',   value: `${fmtNum(data.sintetico.margemMediaPonderada)}%`, color: 'text-purple-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`text-lg font-bold tabular-nums ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead className="text-right">Qtd</TableHead>
              <TableHead className="text-right">Venda</TableHead>
              <TableHead className="text-right">Custo Médio</TableHead>
              <TableHead className="text-right">Lucro Bruto</TableHead>
              <TableHead className="text-right">Margem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={r.produtoId}>
                <TableCell className="font-medium">{r.descricao}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.qtdVendida)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.valorVenda)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{fmtCur(r.custoMedio)}</TableCell>
                <TableCell className={`text-right tabular-nums font-semibold ${r.lucroBruto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {fmtCur(r.lucroBruto)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  <Badge variant="outline" className={r.margemBruta >= 20 ? 'border-green-400 text-green-600' : r.margemBruta >= 0 ? 'border-yellow-400 text-yellow-600' : 'border-red-400 text-red-600'}>
                    {fmtNum(r.margemBruta)}%
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Evolução por Produto ──────────────────────────────────────────────────────

function TabEvolucaoProdutos() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(today());
  const { data, isLoading } = useEvolucaoProdutos({ dtInicio, dtFim });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={setDtInicio} setDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/gerencial/evolucao-produtos/exportar" params={exportParams} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead>Mês/Ano</TableHead>
              <TableHead className="text-right">Qtd Vendida</TableHead>
              <TableHead className="text-right">Total Vendas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => <TableRow key={i}><TableCell colSpan={4}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.length ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{r.descricao}</TableCell>
                <TableCell className="text-muted-foreground">{r.mesDescricao}/{r.ano}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.qtdVendida)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalVendas)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

const RelatoriosGerencial = () => (
  <div className="p-6 space-y-6">
    <div>
      <h1 className="text-3xl font-bold text-foreground">Relatórios Gerenciais</h1>
      <p className="text-muted-foreground mt-1">Curva ABC, lucratividade e evolução por produto.</p>
    </div>
    <Tabs defaultValue="abc-clientes">
      <TabsList className="flex-wrap">
        <TabsTrigger value="abc-clientes"  className="gap-2"><BarChart3   className="h-4 w-4" />ABC Clientes</TabsTrigger>
        <TabsTrigger value="abc-produtos"  className="gap-2"><Package     className="h-4 w-4" />ABC Produtos</TabsTrigger>
        <TabsTrigger value="lucratividade" className="gap-2"><DollarSign  className="h-4 w-4" />Lucratividade</TabsTrigger>
        <TabsTrigger value="evolucao"      className="gap-2"><TrendingUp  className="h-4 w-4" />Evolução / Produto</TabsTrigger>
      </TabsList>
      <TabsContent value="abc-clientes">  <Card><CardHeader><CardTitle>Curva ABC — Clientes</CardTitle></CardHeader><CardContent><TabAbcClientes /></CardContent></Card></TabsContent>
      <TabsContent value="abc-produtos">  <Card><CardHeader><CardTitle>Curva ABC — Produtos</CardTitle></CardHeader><CardContent><TabAbcProdutos /></CardContent></Card></TabsContent>
      <TabsContent value="lucratividade"> <Card><CardHeader><CardTitle>Lucratividade Bruta por Produto</CardTitle></CardHeader><CardContent><TabLucratividade /></CardContent></Card></TabsContent>
      <TabsContent value="evolucao">      <Card><CardHeader><CardTitle>Evolução de Vendas por Produto</CardTitle></CardHeader><CardContent><TabEvolucaoProdutos /></CardContent></Card></TabsContent>
    </Tabs>
  </div>
);

export default RelatoriosGerencial;
